import type { ThemeDefinition } from '../types';

/**
 * Ivory & Brass — the theme the product already wears.
 *
 * The values began as the literals hard-coded in `styles/centraone-theme.css`
 * and `tailwind.config.js` before the theme runtime existed. They were then
 * softened on purpose: body ink sat at ~15:1 on its surface, which read as
 * harsh on warm stone. Ink now targets ~11:1 (light) / ~12:1 (dark), hairlines
 * and status hues are quieter, and every text pairing still clears WCAG AA
 * (4.5:1) except ink-4, which is placeholder-only. Changing a number here
 * changes the live product, so re-check contrast when you do.
 *
 * Warm stone surfaces, a single brass accent at roughly 5% coverage, and
 * near-square geometry (2px, not 8px): borders carry elevation and shadows are
 * a whisper.
 */
export const ivoryBrass: ThemeDefinition = {
  id: 'ivory-brass',
  name: 'Ivory & Brass',
  description: 'Warm stone surfaces with a single brass accent. The current CentraOne appearance.',

  // Ramp values are the literals that used to sit in tailwind.config.js. A
  // ramp spans BOTH appearances — the app writes its dark surfaces as
  // `dark:bg-gray-900` — so these do not change when light/dark flips.
  palette: {
    // brass ramp — was blue
    primary: {
      50: '#FBF6EC', 100: '#F4E9D5', 200: '#E3CB9E', 300: '#CDAE7E', 400: '#BC9763',
      500: '#AA8246', 600: '#977139', 700: '#83663A', 800: '#5E4A2E', 900: '#3A2D1A',
    },
    // warm stone ramp — was slate
    secondary: {
      50: '#FAF6EF', 100: '#F3EDE1', 200: '#E6DFD3', 300: '#DDD4C5', 400: '#A0968A',
      500: '#736A5E', 600: '#5A5247', 700: '#453E33', 800: '#3A342D', 900: '#1C1915',
    },
    // remapped so existing gray-* utilities go warm, not cold
    gray: {
      50: '#F7F3EA', 100: '#F1ECE3', 200: '#E6DFD3', 300: '#DDD4C5', 400: '#A0968A',
      500: '#736A5E', 600: '#5A5247', 700: '#3A342B', 800: '#211E19', 900: '#1C1915',
      950: '#171410',
    },
    brass: {
      DEFAULT: '#AA8246', ink: '#83663A', light: '#BC9763', pale: '#E3CB9E',
    },
    // status only — never decoration
    signal: {
      ok: '#4B7363', okSoft: '#6FA98C',
      warn: '#985B3A', warnSoft: '#D08A63',
      crit: '#96525D', critSoft: '#C48690',
    },
  },

  base: {
    // The display face was a second sans for page titles. It is the text face
    // now: the sidebar is set in the text face, and a heading in a different
    // family beside it is the mismatch users keep reporting. Both themes set
    // IBM Plex Sans, the one self-hosted text face (see centraone-theme.css).
    '--co-font-display': "'IBM Plex Sans', system-ui, sans-serif",
    '--co-font-text':    "'IBM Plex Sans', system-ui, sans-serif",
    '--co-font-mono':    "'JetBrains Mono', ui-monospace, monospace",
    '--co-font-sans':    "var(--co-font-text)",
    '--co-radius':       "2px",
    '--co-radius-lg':    "3px",
    '--co-dur-fast':     "120ms",
    '--co-dur':          "180ms",
    '--co-ease':         "cubic-bezier(.2, .6, .3, 1)",
    // Tailwind's radius scale, published so the utilities can follow the
    // theme. These reproduce the literals the config used to carry, which is
    // why `sm`/`md` are the same 2px as the default and nothing visibly moves.
    '--co-radius-sm':    "2px",
    '--co-radius-md':    "2px",
    '--co-radius-xl':    "3px",
    '--co-radius-2xl':   "4px",
    // The floating-card corner. 10px was inlined in the shared kit's CARD
    // style; naming it here changes no pixel and makes it themeable.
    '--co-radius-card':  "10px",
  },

  light: {
    '--co-canvas':            "#F1ECE3",
    '--co-surface':           "#F7F3EA",
    '--co-page':              "#F7F3EA",
    '--co-raised':            "#FAF6EF",
    '--co-rail':              "#E9E3D8",
    '--co-sunken':            "#EBE5DA",
    '--co-line':              "#DDD4C5",
    '--co-line-strong':       "#CEC3B0",
    '--co-line-subtle':       "#E6DFD3",
    '--co-line-frame':        "#DDD4C5",
    '--co-ink':               "#3A342D",
    '--co-ink-2':             "#5A5247",
    '--co-ink-3':             "#736A5E",
    '--co-ink-3-soft':        "#857A6C",
    '--co-ink-4':             "#A0968A",
    '--co-ink-invert':        "#FAF6EF",
    '--co-avatar':            "#3A342D",
    '--co-selected':          "#F0EADC",
    '--co-brass':             "#AA8246",
    '--co-brass-ink':         "#83663A",
    '--co-brass-hover':       "#977139",
    '--co-brass-wash':        "rgba(170, 130, 70, .08)",
    '--co-brass-on':          "#FBF7F0",
    '--co-green':             "#4B7363",
    '--co-green-soft':        "#6FA98C",
    '--co-green-wash':        "rgba(75, 115, 99, .08)",
    '--co-terracotta':        "#985B3A",
    '--co-terracotta-soft':   "#D08A63",
    '--co-terracotta-wash':   "rgba(152, 91, 58, .08)",
    '--co-burgundy':          "#96525D",
    '--co-neutral-band':      "#EFE7D9",
    '--co-green-line':        "#BED0C5",
    '--co-green-ink':         "#3F6656",
    '--co-terracotta-line':   "#E0C6B8",
    '--co-terracotta-ink':    "#8A5233",
    '--co-burgundy-wash':     "rgba(150, 82, 93, .08)",
    '--co-machine':           "#7C8AA0",
    '--co-machine-ink':       "#5A6779",
    '--co-machine-line':      "#B6BFCC",
    '--co-machine-wash':      "#F1F3F6",
    '--co-burgundy-line':     "#D0A2A6",
    // Was referenced in twelve places and never declared, so every
    // accent-tinted border in the product (selected rows, the bulk bar,
    // brass chips, the KB panels) fell back to `currentColor`. Same pale
    // edge the green/terracotta/burgundy families already have.
    '--co-brass-line':        "#E3CFAE",
    '--co-on-ink':            "#FBF7F0",
    '--co-on-ink-2':          "#C9BDAC",
    '--co-on-ink-line':       "#3A332A",
    '--co-brass-pale':        "#E3CB9E",
    '--co-burgundy-pale':     "#E27F86",
    '--co-burgundy-pale-ink': "#E8B4B9",
    '--co-machine-pale':      "#8FA3BC",
    '--co-shadow':            "0 1px 2px rgba(58, 52, 45, .04)",
    '--co-shadow-pop':        "0 12px 32px rgba(58, 52, 45, .11)",
    '--co-shadow-card':       "0 1px 2px rgba(58, 52, 45, .03), 0 6px 20px rgba(58, 52, 45, .06)",
    'color-scheme': "light",
  },

  dark: {
    '--co-canvas':            "#1C1915",
    '--co-surface':           "#211E19",
    '--co-page':              "#211E19",
    '--co-raised':            "#26221C",
    '--co-rail':              "#201D18",
    '--co-sunken':            "#171410",
    '--co-line':              "#3A342B",
    '--co-line-strong':       "#453E33",
    '--co-line-subtle':       "#2D2821",
    '--co-line-frame':        "#3A342B",
    '--co-ink':               "#E4DCCF",
    '--co-ink-2':             "#C2B8AA",
    '--co-ink-3':             "#9C9286",
    '--co-ink-3-soft':        "#8D8377",
    '--co-ink-4':             "#7E7466",
    '--co-ink-invert':        "#211E19",
    '--co-avatar':            "#9A8F7F",
    '--co-selected':          "#2D2821",
    '--co-brass':             "#BC9763",
    '--co-brass-ink':         "#BC9763",
    '--co-brass-hover':       "#CBA673",
    '--co-brass-wash':        "rgba(188, 151, 99, .08)",
    '--co-brass-on':          "#211E19",
    '--co-green':             "#86B19B",
    '--co-green-soft':        "#6FA98C",
    '--co-green-wash':        "rgba(134, 177, 155, .08)",
    '--co-terracotta':        "#D29C7A",
    '--co-terracotta-soft':   "#D08A63",
    '--co-terracotta-wash':   "rgba(210, 156, 122, .08)",
    '--co-burgundy':          "#C48690",
    '--co-neutral-band':      "#28231C",
    '--co-green-line':        "#35513F",
    '--co-green-ink':         "#86B19B",
    '--co-terracotta-line':   "#5C3826",
    '--co-terracotta-ink':    "#D29C7A",
    '--co-burgundy-wash':     "rgba(196, 134, 144, .10)",
    '--co-machine':           "#8FA0B8",
    '--co-machine-ink':       "#A2AFC0",
    '--co-machine-line':      "#3B4655",
    '--co-machine-wash':      "#1D2026",
    '--co-burgundy-line':     "#5C3038",
    '--co-brass-line':        "#4E3D1F",
    '--co-on-ink':            "#211E19",
    '--co-on-ink-2':          "#5A5348",
    '--co-on-ink-line':       "#D8CEBE",
    '--co-brass-pale':        "#6B4C1E",
    '--co-burgundy-pale':     "#8C3F4A",
    '--co-burgundy-pale-ink': "#6E2F38",
    '--co-machine-pale':      "#3F5470",
    '--co-shadow':            "0 1px 2px rgba(0, 0, 0, .32)",
    '--co-shadow-pop':        "0 12px 32px rgba(0, 0, 0, .4)",
    '--co-shadow-card':       "0 1px 2px rgba(0, 0, 0, .28), 0 6px 20px rgba(0, 0, 0, .36)",
    'color-scheme': "dark",
  },

  preview: {
    light: ['#F1ECE3', '#FAF6EF', '#AA8246', '#3A342D'],
    dark: ['#1C1915', '#26221C', '#BC9763', '#E4DCCF'],
  },
};
